import React from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { Flame, ArrowRight, Clock } from "lucide-react";

function timeAgo(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return `${Math.floor(diff)}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

function cleanSummary(s) {
  return (s || "").replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
}

/**
 * Editorial home hero: "The Street Today".
 * Shows the latest trending news story with its REAL cover image, full title,
 * un-cropped summary (first ~360 chars), source, and a secondary rail of the
 * next 3 trending stories so readers always see fresh content on arrival.
 */
export default function StreetTodayHero() {
  const { data, isLoading } = useQuery({
    queryKey: ["street-today-feed"],
    queryFn: async () => (await api.get("/news?limit=10")).data,
    refetchInterval: 2 * 60_000,
    staleTime: 60_000,
  });

  const items = (data?.data || []).filter(n => n.title);
  // Prefer the first article that has a real cover image for the hero
  const hero = items.find(n => n.image) || items[0];
  const trending = items.filter(n => n.slug !== hero?.slug).slice(0, 3);

  const dateStr = new Date().toLocaleDateString("en-IN", {
    weekday: "short", day: "2-digit", month: "short", year: "numeric",
  });

  return (
    <section className="ns-card p-5 sm:p-7" data-testid="street-today-hero">
      <div className="flex items-start justify-between mb-3 flex-wrap gap-y-2">
        <div className="ns-eyebrow">Your Daily Market Briefing</div>
        <div className="text-[11px] font-mono-fin text-[#8C8D90]">{dateStr}</div>
      </div>

      <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.05] tracking-tight text-[#1A1A1A] mb-2">
        The Street Today
      </h1>
      <p className="text-[#6B6C6F] text-sm sm:text-base mb-5">
        What's moving right now — pulled live from India's newsrooms.
      </p>

      {isLoading && (
        <div className="h-64 flex items-center justify-center text-sm text-[#8C8D90] font-mono-fin">
          Loading today's briefing…
        </div>
      )}

      {hero && (
        <Link
          to={`/news/${hero.slug || hero.id}`}
          className="block group"
          data-testid="hero-top-story-link"
        >
          <div className="relative aspect-[16/9] overflow-hidden rounded-md mb-5 bg-[#F0ECE4]">
            {hero.image ? (
              <img
                src={hero.image}
                alt={hero.title}
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.02]"
                loading="eager"
                onError={(e) => { e.currentTarget.style.display = "none"; }}
              />
            ) : null}
            <div className="absolute top-3 left-3 flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#D91E2A] text-white text-[10px] font-mono-fin uppercase tracking-widest font-bold shadow-sm">
              <Flame className="w-3 h-3" /> Trending
            </div>
            <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between text-[10px] font-mono-fin text-white/90">
              <span className="uppercase tracking-widest">{(hero.category || "Markets")}</span>
              <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {timeAgo(hero.published)}</span>
            </div>
            <div className="absolute inset-0 pointer-events-none bg-gradient-to-t from-black/50 via-transparent to-transparent" />
          </div>

          <h2
            className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold leading-tight text-[#1A1A1A] group-hover:text-[#D91E2A] transition-colors mb-3"
            data-testid="hero-story-title"
          >
            {hero.title}
          </h2>

          <p className="text-[15px] sm:text-base text-[#3A3B3E] leading-relaxed mb-4" data-testid="hero-story-summary">
            {cleanSummary(hero.summary).slice(0, 360)}
            {cleanSummary(hero.summary).length > 360 ? "…" : ""}
          </p>

          <div className="text-xs font-semibold text-[#D91E2A] inline-flex items-center gap-1 group-hover:gap-2 transition-all">
            Read the full briefing <ArrowRight className="w-3.5 h-3.5" />
          </div>
        </Link>
      )}

      {trending.length > 0 && (
        <div className="mt-7 pt-5 border-t border-[#E2DDD3]">
          <div className="ns-eyebrow mb-3 flex items-center gap-1.5">
            <Flame className="w-3 h-3 text-[#D91E2A]" /> Also Trending
          </div>
          <ul className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="trending-rail">
            {trending.map(n => (
              <li key={n.id}>
                <Link
                  to={`/news/${n.slug || n.id}`}
                  className="group block"
                  data-testid={`trending-rail-item-${n.id}`}
                >
                  {n.image ? (
                    <div className="aspect-[16/10] overflow-hidden rounded mb-2 bg-[#F0ECE4]">
                      <img
                        src={n.image}
                        alt={n.title}
                        className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                        loading="lazy"
                        onError={(e) => { e.currentTarget.style.display = "none"; }}
                      />
                    </div>
                  ) : null}
                  <div className="text-[10px] font-mono-fin uppercase tracking-widest text-[#8C8D90] mb-1">
                    {(n.category || "Markets")} · {timeAgo(n.published)}
                  </div>
                  <h3 className="font-heading text-sm font-semibold leading-snug text-[#1A1A1A] group-hover:text-[#D91E2A] line-clamp-3">
                    {n.title}
                  </h3>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}
